import React, { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
    Colors,
    FindLoad,
    FindSuccessItem,
    formatSecondDuration,
    goalDataTypeEnums,
    Images,
    renderIntervalText,
    trialStatusEnums,
    useBaseModal,
} from 'utils';
import { CloseButton, MinLoader, SimpleTooltip } from '../../../../../components';
import { httpRequestsOnLoadActions, httpRequestsOnSuccessActions, sessionActions } from '../../../../../store';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import { CustomModal } from '../../../../../components/modal/modalBase';

export const renderColorValue = (goal, current) => {
    if (goal?.dataType === goalDataTypeEnums.RATE) return 'green';
    if (goal?.dataType === goalDataTypeEnums.FREQUENCY) return 'green';
    if (!current) return 'blue';
    if (goal?.dataType === goalDataTypeEnums.LATENCY) {
        if (current?.label === latencyEnums.CORRECT) return 'green';
        if (current?.label === latencyEnums.INCORRECT) return 'red';
        if (current?.label === latencyEnums.NO_RESPONSE) return 'blue';
    }
    if (goal?.dataType === goalDataTypeEnums.RATING) return 'green';
    if (current?.value === 0) return 'red';
    if (current?.value) return 'green';
    return 'blue';
};

export const renderCardColor = (color) => {
    switch (color) {
        case 'blue':
            return {
                color: '#175CD3',
                background: '#EFF8FF',
            };
        case 'green':
            return {
                color: '#027A48',
                background: '#ECFDF3',
            };

        case 'red':
            return {
                color: '#B42318',
                background: '#FEF3F2',
            };
        default:
            return {
                color: '#175CD3',
                background: '#EFF8FF',
            };
    }
};

export const latencyEnums = {
    CORRECT: 'CORRECT',
    INCORRECT: 'INCORRECT',
    NO_RESPONSE: 'NO_RESPONSE',
};

export const latencyEnumBtn = [
    {
        title: 'Correct',
        value: latencyEnums.CORRECT,
        icon: Images.greenMark,
        class: 'target-status-btn-box',
    },
    {
        title: 'Incorrect',
        value: latencyEnums.INCORRECT,
        icon: Images.remove,
        class: 'target-status-no-response-btn-box',
    },
    {
        title: 'No Response',
        value: latencyEnums.NO_RESPONSE,
        icon: Images.slash,
        class: 'target-status-blue-btn-box',
    },
];

export const formatTime = (seconds) => {
    // Stored durations are derived from milliseconds, so `seconds` is often a
    // float (e.g. 735.775999999999954). Floor to whole seconds before splitting
    // into mm:ss — otherwise the fractional remainder leaks into the display as a
    // long trailing string. Flooring (not rounding) also avoids the "0:60" edge.
    const totalSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

// Locate the most-recent IN_PROGRESS trial returned by the API after a Play save,
// so the timer component can target the same trial id when it later sends Stop.
// We scan from the tail because IN_PROGRESS trials are always appended.
//
// When `staffId` is provided, only trials authored by that clinician match —
// otherwise a second observer working the same target in another tab would
// pick up the first clinician's IN_PROGRESS row and write into it, conflating
// data across users (Rate: A's count overwritten by B's, or vice versa).
export const findInProgressTrialId = (tSummary, staffId) => {
    const trials = tSummary?.trials;
    if (!trials?.length) return null;
    for (let i = trials.length - 1; i >= 0; i -= 1) {
        const t = trials[i];
        if (t?.status !== trialStatusEnums.IN_PROGRESS) continue;
        if (staffId && t?.staffId !== staffId) continue;
        return t.id;
    }
    return null;
};

/**
 * A goal's per-session trial cap (`goal.maxTrials`, null = no limit) for one
 * target card. Two thresholds, on purpose:
 *  - `creationBlocked` counts every trial, IN_PROGRESS included: a running
 *    timer already holds a slot, so Play on another can't open one more.
 *  - `completedAtCap` counts COMPLETED only: it pulls `index` back off the
 *    empty "next trial" slot once the cap is met, without yanking the card off
 *    the slot a still-running timer lives on.
 * FREQUENCY is exempt — the api keeps exactly one trial holding the count.
 */
export const useTrialCap = (goal, item, completedTrials, index, setIndex) => {
    const maxTrials = goal?.maxTrials ?? null;
    const capped = maxTrials != null && goal?.dataType !== goalDataTypeEnums.FREQUENCY;
    const creationBlocked = capped && (item?.tSummary?.trials?.length || 0) >= maxTrials;
    const completedAtCap = capped && completedTrials.length >= maxTrials;

    useEffect(() => {
        if (completedAtCap && index > completedTrials.length) {
            setIndex(Math.max(completedTrials.length, 1));
        }
    }, [completedAtCap, index, completedTrials.length]);

    return { maxTrials, creationBlocked, completedAtCap };
};

export const trialCapMessage = (maxTrials) => `This goal allows at most ${maxTrials} trial${maxTrials === 1 ? '' : 's'} per session.`;

// Pause-aware elapsed: walks the session's `events[]` (start/pause/resume/complete)
// to sum only the seconds the session was actually IN_PROGRESS within
// [startTimeIso, endTimeIso]. Without this, refresh-after-pause would restore a
// timer that includes pause time — clinically wrong (clinician wasn't observing).
// Falls back to wall-clock diff if no events are available.
export const computeActiveElapsedSeconds = (sessionEvents, startTimeIso, endTimeIso) => {
    const wallClockDiff = () => Math.max(0, moment(endTimeIso).diff(moment(startTimeIso), 'seconds'));
    if (!sessionEvents?.length || !startTimeIso) return wallClockDiff();

    const startMs = new Date(startTimeIso).getTime();
    const endMs = new Date(endTimeIso).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) return 0;

    const sorted = [...sessionEvents]
        .map((e) => ({ ms: new Date(e.datetime).getTime(), status: e.status }))
        .filter((e) => !Number.isNaN(e.ms))
        .sort((a, b) => a.ms - b.ms);
    if (!sorted.length) return wallClockDiff();

    // Find session state right at trial.startTime (last event at-or-before start)
    let stateAtStart = 'COMPLETED';
    for (const e of sorted) {
        if (e.ms <= startMs) stateAtStart = e.status;
        else break;
    }

    let activeMs = 0;
    let activeFrom = stateAtStart === 'IN_PROGRESS' ? startMs : null;

    for (const e of sorted) {
        if (e.ms <= startMs) continue;
        if (e.ms >= endMs) {
            if (activeFrom !== null) activeMs += endMs - activeFrom;
            return Math.max(0, Math.floor(activeMs / 1000));
        }
        if (activeFrom !== null) {
            activeMs += e.ms - activeFrom;
            activeFrom = null;
        }
        if (e.status === 'IN_PROGRESS') activeFrom = e.ms;
    }
    if (activeFrom !== null) activeMs += endMs - activeFrom;
    return Math.max(0, Math.floor(activeMs / 1000));
};

// Same scan as findInProgressTrialId but returns the full trial — needed on mount
// so a refreshed component can rehydrate `startTime` and recompute elapsed seconds
// against now. Returns null if no IN_PROGRESS row exists for this target.
// Pass `staffId` to scope to the current clinician — without it, a parallel
// observer's trial would hijack this user's local state on resume-on-mount.
export const findInProgressTrial = (item, staffId) => {
    const trials = item?.tSummary?.trials;
    if (!trials?.length) return null;
    for (let i = trials.length - 1; i >= 0; i -= 1) {
        const t = trials[i];
        if (t?.status !== trialStatusEnums.IN_PROGRESS) continue;
        if (staffId && t?.staffId !== staffId) continue;
        return t;
    }
    return null;
};

// Trailing-edge debounce that always calls the latest closure of `fn` (via ref),
// so callers can update closed-over state (count, activeTrialId) without resetting
// the timer. `flush` runs immediately + cancels the pending tick — used on Stop
// so the final COMPLETED save isn't preceded by a stale IN_PROGRESS write.
export const useDebouncedCallback = (fn, delay) => {
    const fnRef = useRef(fn);
    const timeoutRef = useRef(null);
    fnRef.current = fn;

    const cancel = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const debounced = useCallback(
        (...args) => {
            cancel();
            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                fnRef.current(...args);
            }, delay);
        },
        [cancel, delay],
    );

    useEffect(() => cancel, [cancel]);

    return [debounced, cancel];
};

export const Latency = ({ item, loader, disableActions, capMessage, saveTSummary, current, goal, index, grid, width }) => {
    const [timer, setTimer] = useState(current ? current?.value : null);
    const intervalRef = useRef(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const timerValue = goal?.dataType === goalDataTypeEnums.LATENCY ? goal?.options[0] : null;
    const maxTime = timerValue?.value;
    // Trial id of the IN_PROGRESS row created when the user pressed Play. Used
    // to update the same row to COMPLETED on response/timeout so the trial keeps
    // a single id (and a single { startTime, endTime } pair) instead of fragmenting.
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startTimeRef = useRef(null);
    const startPromiseRef = useRef(null);
    // Single-fire latch: protects against fast double-taps (Correct + Incorrect
    // before React re-renders) and against the auto-timeout effect racing with a
    // user click. Without this, two completeTrial() calls could fire — and if the
    // second runs after activeTrialId has been cleared, the parent's fallback
    // `current?.id || null` path would create a SECOND trial with a different label.
    const submittingRef = useRef(false);
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);

    useEffect(() => {
        // Always reset on slot change to !current. The previous `if (activeTrialId)
        // return` guard was meant to preserve a mid-trial state across nav, but it
        // had a worse failure mode: navigating prev → next on an empty slot left
        // timer non-null + activeTrialId set, which the interval effect happily
        // re-started, making the timer "auto-tick" on simple navigation. Dropping
        // the guard means navigating away from the IN_PROGRESS slot loses local
        // state, but handlePlay below picks up an existing IN_PROGRESS instead of
        // duplicating, so the trial isn't lost — just the local elapsed display.
        if (!current) {
            setTimer(null);
            setActiveTrialId(null);
            startTimeRef.current = null;
            startPromiseRef.current = null;
            submittingRef.current = false;
        }
    }, [current, index]);

    // Resume-on-mount uses session events for pause-aware elapsed; clamps to
    // maxTime so the auto-NO_RESPONSE effect fires cleanly post-refresh if the
    // window already closed. Only fires when on the empty next-slot — never on
    // a completed slot (would auto-resume a stale IN_PROGRESS while user is
    // viewing history).
    useEffect(() => {
        if (current) return;
        if (activeTrialId || timer !== null) return;
        const inProgress = findInProgressTrial(item);
        if (!inProgress?.startTime) return;
        const elapsedRaw = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
        const elapsed = maxTime ? Math.min(maxTime, Math.max(0, elapsedRaw)) : Math.max(0, elapsedRaw);
        setActiveTrialId(inProgress.id);
        startTimeRef.current = inProgress.startTime;
        setTimer(elapsed);
    }, [item?.tSummary?.trials, sessionEvents, current]);

    useEffect(() => {
        // Don't auto-fire NO_RESPONSE while session is paused — clinician can't
        // intervene. Once session resumes, the timer resumes ticking and this
        // effect re-fires when timer hits maxTime.
        if (!current && timer !== null && timer === maxTime && !disableActions) {
            if (submittingRef.current) return;
            const noResponse = latencyEnumBtn.find((i) => i.value === latencyEnums.NO_RESPONSE);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            completeTrial(noResponse?.value);
        }
    }, [timer, disableActions]);

    useEffect(() => {
        if ((timer || timer === 0) && !current && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => {
                    if (prev + 1 >= maxTime) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                        return maxTime;
                    }
                    return prev + 1;
                });
            }, 1000);
        }
        return () => clearInterval(intervalRef.current);
    }, [timer, current, maxTime, disableActions]);

    // Persist the timer the moment Play is pressed so startTime survives a
    // browser crash. We also stash the id locally and on the resolved Promise
    // to handle a fast-tap scenario where Stop fires before Play's response.
    const handlePlay = () => {
        if (capMessage) return;
        if (disableActions) return;
        const startTime = moment().format();
        startTimeRef.current = startTime;
        setTimer(0);
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    const completeTrial = async (label) => {
        // Latch must flip BEFORE the await, otherwise a second click during the
        // await window slips past the guard in handleButtonClick.
        if (submittingRef.current || disableActions) return;
        submittingRef.current = true;
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        const endTime = moment().format();
        saveTSummary({
            trialId: trialId || undefined,
            label,
            value: timer || 1,
            startTime: startTimeRef.current || endTime,
            endTime,
            status: trialStatusEnums.COMPLETED,
        });
        setActiveTrialId(null);
        startPromiseRef.current = null;
        // submittingRef stays true until the index advance / current change resets
        // it via the effect on [current, index]; that gates the next trial slot.
    };

    const handleButtonClick = (label) => {
        if (submittingRef.current) return;
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        completeTrial(label);
    };

    return (
        <div className="full-width">
            {!current && timer === null ? (
                <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                <span style={{ display: 'inline-flex', width: width ? width : grid ? undefined : '100%' }}>
                <button
                    disabled={disableActions || !!capMessage}
                    onClick={handlePlay}
                    style={{ width: width ? width : grid ? '145px' : '100%', opacity: disableActions || capMessage ? 0.5 : 1 }}
                    className="play-btn-box"
                >
                    <img src={Images.play} alt="play-icon" />
                </button>
                </span>
                </SimpleTooltip>
            ) : (
                <div className={`flex-align-center ${grid ? 'column' : ''}`} style={{ gap: 24 }}>
                    <div className="timer-box">{formatTime(current?.value ? current?.value : timer)}</div>
                    <div className={`flex-align-center ${grid ? 'column' : ''}`} style={{ gap: 16 }}>
                        {latencyEnumBtn.map((i, j) => (
                            <button
                                disabled={loading || disableActions}
                                key={j}
                                className={`${i.class} ${current?.label === i.value ? 'active' : ''}`}
                                style={{ width: 145, opacity: loading || disableActions ? 0.5 : 1 }}
                                onClick={() => handleButtonClick(i.value)}
                            >
                                {/*{(loading && loader?.label === i?.value) ? (*/}
                                {loading && loading?.label === i?.value ? (
                                    <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                                ) : (
                                    <>
                                        <img src={i.icon} alt="" style={{ width: 20, height: 20 }} />
                                        {i.title}
                                    </>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const Duration = ({ item, loader, disableActions, capMessage, saveTSummary, grid, index }) => {
    const [timer, setTimer] = useState(0);
    const [play, setPlay] = useState(false);
    const intervalRef = useRef(null);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const prevLoadingRef = useRef(loading);
    const playRef = useRef(false);
    // Session events drive pause-aware elapsed math on resume. We read them once
    // here (not via a hook) to keep the timer trial-local and self-contained.
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);

    useEffect(() => {
        playRef.current = play;
    }, [play]);

    useEffect(() => {
        const wasLoading = prevLoadingRef.current;
        prevLoadingRef.current = loading;
        if (wasLoading && !loading && !playRef.current) {
            setTimer(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
        }
    }, [loading]);

    // Tick gated on `!disableActions` so the local visible timer freezes the
    // moment the session is paused — clinician's Duration value reflects only
    // active observation time. Resume from pause continues from the same value.
    useEffect(() => {
        if (play && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [play, disableActions]);

    // Combined resume-on-mount + cross-tab sync: hydrate state if no active trial
    // yet, OR wind down if our active trial was completed in another tab/device.
    // Without the wind-down branch, Tab A's timer keeps ticking after Tab B hits
    // Stop — local play stays true forever because the original guard prevented
    // the effect from re-evaluating once activeTrialId was set.
    useEffect(() => {
        const inProgress = findInProgressTrial(item);
        if (!activeTrialId) {
            if (play || !inProgress?.startTime) return;
            const elapsed = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
            setActiveTrialId(inProgress.id);
            setStartEndTime({ startTime: inProgress.startTime, endTime: null });
            setTimer(elapsed);
            setPlay(true);
            return;
        }
        if (!inProgress || inProgress.id !== activeTrialId) {
            // Trial completed (or deleted) elsewhere — stop ticking locally so
            // the timer doesn't drift past the COMPLETED value in DB.
            setPlay(false);
            setTimer(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
        }
    }, [item?.tSummary?.trials, sessionEvents]);

    const handlePlay = () => {
        if (capMessage) return;
        if (disableActions) return;
        const startTime = moment().format();
        setTimer(0);
        setPlay(true);
        setStartEndTime({ startTime, endTime: null });
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    const handleStop = async () => {
        if (disableActions) return;
        const endTime = moment().format();
        setPlay(false);
        setStartEndTime((prev) => ({ ...prev, endTime }));
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary({
            trialId: trialId || undefined,
            value: timer || 1,
            startTime: startEndTime?.startTime,
            endTime,
            status: trialStatusEnums.COMPLETED,
        });
    };

    return (
        <div className="full-width">
            <div className={grid ? 'flex-align-center' : 'space-between'} style={{ gap: 16 }}>
                <div className="flex-align-center" style={{ gap: 16 }}>
                    {!play && !loading ? (
                        <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                        <span style={{ display: 'inline-flex' }}>
                        <button
                            className="play-btn-box"
                            disabled={disableActions || !!capMessage}
                            style={{ width: 40, opacity: disableActions || capMessage ? 0.5 : 1 }}
                            onClick={handlePlay}
                        >
                            <img src={Images.play} alt="play-icon" />
                        </button>
                        </span>
                        </SimpleTooltip>
                    ) : (
                        <button
                            disabled={loading}
                            style={{ height: 40, width: 40, opacity: loading ? 0.5 : 1 }}
                            className="session-gray-action-btn-box"
                            onClick={handleStop}
                        >
                            {loading ? (
                                <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                            ) : (
                                <img src={Images.pauseCircle} alt="pause" />
                            )}
                        </button>
                    )}
                    <div className="timer-box">{formatTime((item?.tSummary?.value || 0) + timer)}</div>
                </div>
                {/*{!!item?.tSummary?.trials?.length && (*/}
                {/*    <button*/}
                {/*        style={{ height: 36, width: 36 }}*/}
                {/*        className="session-gray-action-btn-box"*/}
                {/*        onClick={() => handleDurationHistory(item)}*/}
                {/*    >*/}
                {/*        <img src={Images.historyTab} alt="resetTimmer" />*/}
                {/*    </button>*/}
                {/*)}*/}
            </div>
        </div>
    );
};

export const GridDuration = ({ index, btnTitle, item, loader, disableActions, capMessage, saveTSummary, setTimerInfo }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openPopper = Boolean(anchorEl);
    const id = openPopper ? 'simple-popover' : undefined;
    const [timer, setTimer] = useState(0);
    const [play, setPlay] = useState(false);
    const intervalRef = useRef(null);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const prevLoadingRef = useRef(loading);
    const playRef = useRef(false);
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);

    useEffect(() => {
        playRef.current = play;
    }, [play]);

    useEffect(() => {
        const wasLoading = prevLoadingRef.current;
        prevLoadingRef.current = loading;
        if (wasLoading && !loading && !playRef.current) {
            setTimer(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
        }
    }, [loading]);

    useEffect(() => {
        if (play && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [play, disableActions]);

    // Combined resume-on-mount + cross-tab sync — see Duration (list variant).
    useEffect(() => {
        const inProgress = findInProgressTrial(item);
        if (!activeTrialId) {
            if (play || !inProgress?.startTime) return;
            const elapsed = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
            setActiveTrialId(inProgress.id);
            setStartEndTime({ startTime: inProgress.startTime, endTime: null });
            setTimer(elapsed);
            setPlay(true);
            setTimerInfo({ ...item, active: true, startTime: inProgress.startTime });
            return;
        }
        if (!inProgress || inProgress.id !== activeTrialId) {
            // Trial completed elsewhere — wind down so the timer doesn't keep
            // ticking past the COMPLETED value.
            setPlay(false);
            setTimer(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
            setTimerInfo({});
        }
    }, [item?.tSummary?.trials, sessionEvents]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePlay = () => {
        if (capMessage) return;
        const startTime = moment().format();
        setTimer(0);
        setPlay(true);
        setStartEndTime({ startTime, endTime: null });
        setTimerInfo({ ...item, active: true, startTime });
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    const handleStop = async () => {
        const endTime = moment().format();
        setPlay(false);
        setStartEndTime((prev) => ({ ...prev, endTime }));
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary(
            {
                trialId: trialId || undefined,
                value: timer || 1,
                startTime: startEndTime?.startTime,
                endTime,
                status: trialStatusEnums.COMPLETED,
            },
            handleClose,
        );
        // activeTrialId/startPromiseRef are cleared by the prevLoadingRef effect
        // once the COMPLETED save resolves and play is false.
    };

    return (
        <>
            <div style={{ position: 'relative' }}>
                <button aria-describedby={id} onClick={handleClick}>
                    {btnTitle && <p>{btnTitle}</p>}
                </button>

                <Popover
                    id={id}
                    open={openPopper}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            minWidth: 150,
                            borderRadius: '8px',
                        },
                    }}
                >
                    <div className="manage-actions-popover">
                        <div className="manage-actions-popover-body">
                            <div className="full-width">
                                <div className={'flex-align-center'} style={{ gap: 16 }}>
                                    <div className="flex-align-center" style={{ gap: 16 }}>
                                        {!play && !loading ? (
                                            <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                                            <span style={{ display: 'inline-flex' }}>
                                            <button
                                                className="play-btn-box"
                                                disabled={disableActions || !!capMessage}
                                                style={{ width: 40, opacity: disableActions || capMessage ? 0.5 : 1 }}
                                                onClick={handlePlay}
                                            >
                                                <img src={Images.play} alt="play-icon" />
                                            </button>
                                            </span>
                                            </SimpleTooltip>
                                        ) : (
                                            <button
                                                disabled={loading}
                                                style={{ height: 40, width: 40, opacity: loading ? 0.5 : 1 }}
                                                className="session-gray-action-btn-box"
                                                onClick={handleStop}
                                            >
                                                {loading ? (
                                                    <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                                                ) : (
                                                    <img src={Images.pauseCircle} alt="pause" />
                                                )}
                                            </button>
                                        )}
                                        <div
                                            className="timer-box">{formatTime((item?.tSummary?.value || 0) + timer)}</div>
                                    </div>
                                    {/*{!!item?.tSummary?.trials?.length && (*/}
                                    {/*    <button*/}
                                    {/*        style={{ height: 36, width: 36 }}*/}
                                    {/*        className="session-gray-action-btn-box"*/}
                                    {/*        onClick={() => handleDurationHistory(item)}*/}
                                    {/*    >*/}
                                    {/*        <img src={Images.historyTab} alt="resetTimmer" />*/}
                                    {/*    </button>*/}
                                    {/*)}*/}
                                </div>
                            </div>
                        </div>
                    </div>
                </Popover>
            </div>
        </>
    );
};

/** Frequency is capped by the numeric input's own two-digit range, same as before. */
const FREQUENCY_MAX = 99;

/**
 * Frequency counter — the clinician can tap +/- OR type the count directly.
 *
 * The backend stores ONE trial holding the session's count and expects `value` to be that
 * count IN FULL, never a delta, so every write here sends an absolute number: `+` sends
 * `total + 1`, `-` sends `total - 1`, and typing sends what was typed. That is what the field
 * actually means — editing a box reading 5 to say 10 is "the total is 10", not "add 10".
 *
 * `pending` holds the number we last sent. Without it, tapping `+` twice before the first
 * response lands would read the same stale `total` twice and send the same number twice,
 * losing a tap — the old per-tap model was immune because the server summed the deltas.
 *
 * `draft` holds in-progress typing so the field doesn't fight the user mid-keystroke; it is
 * committed on blur or Enter and never on every keystroke, which would fire a request per
 * digit ("1" then "10" would briefly persist 1).
 */
export const Frequency = ({ item, loader, disableActions, saveTSummary }) => {
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const serverCount = item?.tSummary ? item?.tSummary?.trials?.reduce((acc, curr) => acc + curr.value, 0) : 0;

    const [pending, setPending] = useState(null);
    const [draft, setDraft] = useState(null);

    const total = pending ?? serverCount;
    const isBusy = !!loading || disableActions;

    // Drop the optimistic value once the server agrees with it, or once nothing is in flight —
    // the latter covers our write failing and another clinician's write landing instead.
    useEffect(() => {
        if (pending === null) return;
        if (serverCount === pending || !loading) setPending(null);
    }, [serverCount, pending, loading]);

    const commit = (next) => {
        const value = Math.min(FREQUENCY_MAX, Math.max(0, Math.round(Number(next) || 0)));
        if (value === total) return;
        setPending(value);
        saveTSummary({ value });
    };

    return (
        <div className="flex-align-center full-width" style={{ gap: 16 }}>
            <div className="flex-align-center">
                <button
                    disabled={total === 0 || isBusy}
                    style={{
                        height: 40,
                        width: 40,
                        minWidth: 40,
                        opacity: total === 0 || isBusy ? 0.5 : 1,
                    }}
                    className="session-gray-action-btn-box"
                    onClick={() => {
                        setDraft(null);
                        commit(total - 1);
                    }}
                >
                    <img src={Images.minusRemoveCircle} alt="decrease" />
                </button>
                <input
                    disabled={isBusy}
                    className="frequency-count-input-box"
                    type="number"
                    min={0}
                    max={FREQUENCY_MAX}
                    value={draft ?? total}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => {
                        if (draft === null) return;
                        // An emptied field means "no change", not "zero".
                        if (draft !== '') commit(draft);
                        setDraft(null);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') setDraft(null);
                    }}
                />
                <button
                    disabled={total >= FREQUENCY_MAX || isBusy}
                    style={{
                        height: 40,
                        width: 40,
                        minWidth: 40,
                        opacity: total >= FREQUENCY_MAX || isBusy ? 0.5 : 1,
                    }}
                    className="session-gray-action-btn-box"
                    onClick={() => {
                        setDraft(null);
                        commit(total + 1);
                    }}
                >
                    <img src={Images.addCircle} alt="increase" />
                </button>
            </div>
        </div>
    );
};

export const Percent = ({ item, loader, disableActions, capMessage, goal, current, saveTSummary, grid, index }) => {
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;

    return (
        <div className={grid ? 'list-target-btn-wrapper' : 'target-status-btn-wrapper'}>
            {goal?.options?.map((i, j) => {
                const isSelected = i?.label === current?.label;
                if (i?.deletedAt && !isSelected) return null;
                // An option on the empty slot starts a new trial — blocked at the cap.
                const isDisabled = loading || disableActions || !!i?.deletedAt || (!current && !!capMessage);
                return (
                    <SimpleTooltip key={j} title={(!current && capMessage) || ''} placement="top" arrow>
                    <span style={{ display: 'inline-flex' }}>
                    <button
                        style={{ opacity: isDisabled ? 0.5 : 1 }}
                        disabled={isDisabled}
                        onClick={() => saveTSummary(i)}
                        key={j}
                        className={
                            i?.value === 0
                                ? `target-status-no-response-btn-box ${isSelected ? 'active' : ''}`
                                : `target-status-btn-box ${isSelected ? 'active' : ''}`
                        }
                    >
                        {loading && loading?.label === i?.label ? (
                            <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                        ) : (
                            i?.label
                        )}
                    </button>
                    </span>
                    </SimpleTooltip>
                );
            })}
        </div>
    );
};

export const Rating = ({ item, goal, disableActions, capMessage, current, saveTSummary, grid, index, loader }) => {
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;

    return (
        <div className={grid ? 'list-target-btn-wrapper' : 'target-status-btn-wrapper'}>
            {goal?.options?.map((i, j) => {
                const isSelected = i?.label === current?.label;
                if (i?.deletedAt && !isSelected) return null;
                // An option on the empty slot starts a new trial — blocked at the cap.
                const isDisabled = loading || disableActions || !!i?.deletedAt || (!current && !!capMessage);
                return (
                    <SimpleTooltip key={j} title={(!current && capMessage) || ''} placement="top" arrow>
                    <span style={{ display: 'inline-flex' }}>
                    <button
                        disabled={isDisabled}
                        style={{
                            minWidth: grid ? '92px' : '100%',
                            opacity: isDisabled ? 0.5 : 1,
                        }}
                        onClick={() => saveTSummary(i)}
                        key={j}
                        className={`target-status-blue-btn-box ${isSelected ? 'active' : ''}`}
                    >
                        {loading && loading?.label === i?.label ? (
                            <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                        ) : (
                            `${i?.label} (${i?.value})`
                        )}
                    </button>
                    </span>
                    </SimpleTooltip>
                );
            })}
        </div>
    );
};

export const Scale = ({ loader, item, disableActions, capMessage, saveTSummary, current, grid, index }) => {
    const [scaleCount, setScaleCount] = useState(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    // const loading = loader && item?.id === loader?.targetId;
    // const loading = loader && (item?.tSummary?.targetId || item?.tSummary?.id) === loader?.targetId;

    useEffect(() => {
        if (current && current?.value) {
            setScaleCount(current?.value);
        } else {
            setScaleCount(null);
        }
    }, [current, index]);

    return (
        <div className="flex-align-center" style={{ gap: 16 }}>
            {current?.value ? (
                <div className="score-current-count-box" style={{ width: '100%' }}>
                    {current?.value}
                </div>
            ) : (
                <div className="flex-align-center">
                    <input
                        style={{ width: grid ? 80 : 180, opacity: disableActions ? 0.5 : 1 }}
                        disabled={loading || disableActions}
                        placeholder="25*"
                        className="score-count-input-box"
                        type="number"
                        onChange={(e) => {
                            let value = Number(e.target.value) || 0;
                            if (value > 99) value = 99;
                            setScaleCount(value);
                        }}
                        value={scaleCount || ''}
                    />
                </div>
            )}

            {!current && scaleCount !== null && scaleCount > 0 && !disableActions && (
                <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                <span style={{ display: 'inline-flex' }}>
                <button
                    disabled={loading || !!capMessage}
                    style={{ width: 92, opacity: loading || capMessage ? 0.5 : 1 }}
                    className="play-btn-box"
                    onClick={() => {
                        saveTSummary({ value: +scaleCount });
                    }}
                >
                    {loading ? (
                        <MinLoader margin={'0'} color={'#fff'} position={'relative'} />
                    ) : (
                        <>
                            <img src={Images.checkmarkWhite} alt="play-icon" />
                            <p>Save</p>
                        </>
                    )}
                </button>
                </span>
                </SimpleTooltip>
            )}
        </div>
    );
};

export const WholeInterval = ({
                                  item,
                                  loader,
                                  disableActions,
                                  capMessage,
                                  saveTSummary,
                                  current,
                                  goal,
                                  column,
                                  index,
                                  setTimerInfo,
                              }) => {
    const dispatch = useDispatch();
    const [timer, setTimer] = useState(null);
    const [play, setPlay] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [selectedValue, setSelectedValue] = useState(false);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    // Single-fire latch — see Latency for rationale; protects Yes vs No double-tap
    // and the auto-trigger modal racing a manual click after timer === maxTime.
    const submittingRef = useRef(false);
    // Guards against the hydrate effect re-starting the timer after a cancel.
    // Set true in handleCancel, cleared in WIND-DOWN (delete landed) or handlePlay.
    const cancellingRef = useRef(false);
    const intervalRef = useRef(null);
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);
    const sessionId = useSelector((state) => state.session.sessionById?.id);
    const loadDelete = FindLoad('DELETE_TRIAL_LOG')
    // Interval types (Whole/Partial/Momentary) use a SHARED trial across all
    // clinicians observing the same target — whoever starts the timer, anyone
    // can answer Yes/No. `findInProgressTrial` is called without a staffId so
    // it returns the room's single active trial, not just trials authored by
    // this user. Concurrent Yes/No clicks resolve via optimistic-concurrency
    // in `updateTrialAtomic` (first writer wins, second silently no-ops).
    const timerValue =
        goal?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
        goal?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
        goal?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING
            ? goal?.options[0]
            : null;
    const maxTime = timerValue?.value;
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const text = renderIntervalText(goal?.dataType);
    const [showModal, setShowModal] = useState(false);
    const inProgress = findInProgressTrial(item);

    useEffect(() => {
        if (current?.id) {
            setTimer(null);
            setDisabled(true);
            setPlay(false);
            setSelectedValue(false);
            setActiveTrialId(null);
            startPromiseRef.current = null;
            submittingRef.current = false;
            // Cross-tab sync: if Tab A answered Yes/No, Tab B's auto-trigger
            // modal would otherwise stay open even though the trial is now
            // COMPLETED. Close it explicitly when current materialises.
            setShowModal(false);
        } else {
            setTimer(null);
            setPlay(false);
            setDisabled(false);
            setSelectedValue(false);
            setActiveTrialId(null);
            startPromiseRef.current = null;
            submittingRef.current = false;
            cancellingRef.current = false;
            setShowModal(false);
        }
    }, [current, index]);

    useEffect(() => {
        // Don't auto-pop the Yes/No modal while session is paused — clinician
        // can't respond. Once session resumes, the timer ticks past maxTime and
        // this effect re-fires.
        if (!current && !disabled && timer === maxTime && !disableActions) {
            setShowModal(true);
            setDisabled(true);
            setPlay(false);
        }
    }, [timer, current, maxTime, disableActions]);

    useEffect(() => {
        if (play && !current && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => {
                    if (prev + 1 >= maxTime) {
                        clearInterval(intervalRef.current);
                        setPlay(false);
                        return maxTime;
                    }
                    return prev + 1;
                });
            }, 1000);
        }

        return () => clearInterval(intervalRef.current);
    }, [play, current, maxTime, disableActions]);

    // Combined hydrate-or-sync — owns local timer/play state for the
    // IN_PROGRESS trial in three branches:
    //   1. WIND-DOWN: no IN_PROGRESS in DB but we have a local activeTrialId.
    //      Trial was completed/deleted elsewhere; clear local state so the
    //      timer doesn't keep ticking against a non-existent trial.
    //   2. HYDRATE: !activeTrialId OR id-changed (e.g. Tab A started a new
    //      trial after our previous one completed). Reset timer to the
    //      current trial's elapsed-or-checkpoint.
    //   3. LIVE-SYNC: same trial, mirror server isPaused into local play.
    //      Catches cross-tab/cross-user Pause-Resume where Tab A toggles and
    //      Tab B needs to freeze/unfreeze its ticking timer.
    useEffect(() => {

        if (!inProgress?.startTime) {
            if (activeTrialId || cancellingRef.current) {
                setActiveTrialId(null);
                setPlay(false);
                setTimer(null);
                setDisabled(false);
                setSelectedValue(false);
                setShowModal(false);
                startPromiseRef.current = null;
                submittingRef.current = false;
                cancellingRef.current = false;
                setTimerInfo({});
            }
            return;
        }
        const clamp = (v) => (maxTime ? Math.min(maxTime, Math.max(0, v)) : Math.max(0, v));
        const remoteIsPaused = !!inProgress.isPaused;

        // Skip re-hydration while a cancel is in-flight — the create response
        // can land before the delete, which would re-start the timer.
        if (cancellingRef.current) return;

        if (!activeTrialId || inProgress.id !== activeTrialId) {
            const checkpoint = Number(inProgress.value) || 0;
            const elapsedFromEvents = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
            const elapsed = remoteIsPaused ? clamp(checkpoint) : clamp(elapsedFromEvents);
            setActiveTrialId(inProgress.id);
            setStartEndTime({ startTime: inProgress.startTime, endTime: null });
            setTimer(elapsed);
            setShowModal(false);
            if (remoteIsPaused) {
                setPlay(false);
                setTimerInfo({});
            } else if (maxTime && elapsed >= maxTime) {
                setPlay(false);
            } else {
                setPlay(true);
                dispatch(httpRequestsOnLoadActions.appendLoading(item?.id, item));
                setTimerInfo({ ...item, active: true, startTime: inProgress.startTime });
            }
            return;
        }

        if (remoteIsPaused && play) {
            const checkpoint = Number(inProgress.value) || 0;
            setPlay(false);
            setTimer(clamp(checkpoint));
            setTimerInfo({});
        } else if (!remoteIsPaused && !play && timer !== null && (!maxTime || timer < maxTime)) {
            setPlay(true);
            setTimerInfo({ ...item, active: true, startTime: inProgress.startTime });
        }
    }, [item?.tSummary?.trials, maxTime, sessionEvents]);

    // Reuse an existing IN_PROGRESS trial when the user taps Play after a
    // local pause (or on a fresh component instance that lost local state).
    // Backend de-duplicates too, but doing it client-side keeps the timer
    // continuous and avoids a redundant round-trip that would briefly show 0s
    // before the response lands. On resume-from-paused we also shift
    // trial.startTime to (now - elapsed) so future computeActiveElapsedSeconds
    // (which is session-events-based and can't see local trial pauses) returns
    // the correct elapsed across refreshes.
    const handlePlay = () => {
        if (capMessage) return;
        if (disableActions) return;
        cancellingRef.current = false;
        const existing = findInProgressTrial(item);
        if (existing?.startTime) {
            const wasPaused = !!existing.isPaused;
            const checkpoint = wasPaused
                ? (existing.value ?? 0)
                : computeActiveElapsedSeconds(sessionEvents, existing.startTime, moment().format());
            const elapsed = maxTime ? Math.min(maxTime, Math.max(0, checkpoint)) : Math.max(0, checkpoint);
            const newStartTime = wasPaused ? moment().subtract(elapsed, 'seconds').format() : existing.startTime;
            setActiveTrialId(existing.id);
            setStartEndTime({ startTime: newStartTime, endTime: null });
            setTimer(elapsed);
            setPlay(true);
            setTimerInfo({ ...item, active: true, startTime: newStartTime });
            if (wasPaused) {
                saveTSummary({
                    trialId: existing.id,
                    value: elapsed,
                    startTime: newStartTime,
                    status: trialStatusEnums.IN_PROGRESS,
                    isPaused: false,
                });
            }
            return;
        }
        const startTime = moment().format();
        if (timer === null) setTimer(0);
        setPlay(true);
        setStartEndTime({ startTime, endTime: null });
        dispatch(httpRequestsOnLoadActions.appendLoading(item?.id, item));
        setTimerInfo({ ...item, active: true, startTime });
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    // Cancel deletes the active IN_PROGRESS trial via the existing removeTrial
    // API. Local state clears immediately so the UI reflects the cancel without
    // waiting for the round-trip; cross-tab observers receive the UPDATE_SESSIONS
    // socket emit from removeTrial and converge via the WIND-DOWN branch below.
    // Awaits startPromiseRef to cover fast Cancel-right-after-Play before the
    // create response has assigned activeTrialId.
    const handleCancel = async () => {
        cancellingRef.current = true;
        setPlay(false);
        setTimer(null);
        setDisabled(false);
        setSelectedValue(false);
        setStartEndTime({ startTime: null, endTime: null });
        setTimerInfo({});
        setShowModal(false);
        submittingRef.current = false;
        setActiveTrialId(null);
        startPromiseRef.current = null;
        if (!inProgress?.id || !item?.tSummary?.id) return;
        dispatch(sessionActions.deleteTrialLog(sessionId, item?.tSummary?.id, inProgress?.id));
    };

    const handleSaveValue = async (value) => {
        if (submittingRef.current || disableActions) return;
        submittingRef.current = true;
        const endTime = moment().format();
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary({
            trialId: trialId || undefined,
            value,
            startTime: startEndTime?.startTime,
            endTime,
            status: trialStatusEnums.COMPLETED,
        });
        setSelectedValue(value);
        setPlay(false);
        setTimer(null);
        setStartEndTime((prev) => ({ ...prev, endTime }));
        setActiveTrialId(null);
        startPromiseRef.current = null;
        setShowModal(false);
    };

    return (
        <>
            <div className="full-width">
                <div className={column ? 'column' : 'flex-align-center'} style={{ gap: 16 }}>
                    {!current && (
                        <div>
                            {
                            //     !!disabled ? (
                            //     <p className="interval-response-text">{text}</p>
                            // ) : (
                                <div className="flex-align-center" style={{ gap: 16 }}>
                                    {!play ? (
                                        <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                                        <span style={{ display: 'inline-flex' }}>
                                        <button
                                            disabled={disableActions || loadDelete?.length || !!capMessage}
                                            style={{ opacity: (disableActions || loadDelete?.length) || capMessage ? 0.5 : 1 }}
                                            className="play-btn-box"
                                            onClick={handlePlay}
                                        >
                                            <img src={Images.play} alt="play-icon" />
                                        </button>
                                        </span>
                                        </SimpleTooltip>
                                    ) : (
                                        <button
                                            disabled={!inProgress || loadDelete?.length}
                                            style={{opacity: !inProgress ? 0.5 : 1}}
                                            className="play-btn-box"
                                            onClick={handleCancel}
                                        >
                                            <img
                                                style={{
                                                    filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7443%) hue-rotate(96deg) brightness(104%) contrast(101%)',
                                                }}
                                                src={Images.remove}
                                                alt="cancel"
                                            />
                                        </button>
                                    )}
                                    <div className="timer-box">
                                        {formatTime(timer)}
                                        {/*{formatTime((item?.tSummary?.value || 0) + (timer ?? 0))}*/}
                                    </div>
                                </div>
                            }
                        </div>
                    )}

                    <div className="flex-align-center" style={{ gap: 16 }}>
                        <button
                            onClick={() => handleSaveValue(1)}
                            style={{ opacity: !disabled || disableActions ? 0.5 : 1, width: 76 }}
                            disabled={!disabled || disableActions}
                            className={`target-status-blue-btn-box ${current?.value === 1 ? 'active' : ''}`}
                        >
                            {!!loading && selectedValue === 1 ? (
                                <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                            ) : (
                                <>
                                    <img src={Images.checkmarkWhite} alt="mark" />
                                    Yes
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => handleSaveValue(0)}
                            style={{ opacity: !disabled || disableActions ? 0.5 : 1, width: 76 }}
                            disabled={!disabled || disableActions}
                            className={`target-status-no-response-btn-box ${current?.value === 0 ? 'active' : ''}`}
                        >
                            {!!loading && selectedValue === 0 ? (
                                <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                            ) : (
                                <>
                                    <img src={Images.remove} alt="mark" style={{ width: 20, height: 20 }} />
                                    No
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <CustomModal
                handleOpenClose={() => setShowModal(false)}
                openDefault={showModal}
                content={
                    <div style={{ outline: 'none' }} className="modal-wrapper-style open-sans">
                        <div className="modal-body-style-small">
                            <IntervalEnded goal={goal} item={item} saveTSummary={handleSaveValue} />
                        </div>
                    </div>
                }
            />
        </>
    );
};

export const IntervalEnded = ({ item, goal, saveTSummary }) => {
    const { closeBase } = useBaseModal();
    const text = renderIntervalText(goal?.dataType);
    const handleSaveValue = (value) => {
        saveTSummary(value);
        closeBase();
    };

    return (
        <div style={{ width: 380 }}>
            <p className="target-title">{`${item?.name} Timer Ended`}</p>
            <p className="target-interval-text">{text}</p>
            <div className="flex-align-center" style={{ gap: 16 }}>
                <button onClick={() => handleSaveValue(1)} style={{ width: '100%', height: 40 }}
                        className={`target-status-blue-btn-box`}>
                    <img src={Images.checkmarkWhite} alt="mark" />

                    {/*{(!!loading && selectedValue === 1) ?*/}
                    {/*    <MinLoader margin={'0'} color={'#414651'} position={'relative'} />*/}
                    {/*    :*/}
                    {/*    <>*/}
                    {/*        <img src={Images.checkmarkWhite} alt="mark" />*/}
                    {/*        Yes*/}
                    {/*    </>*/}
                    {/*}*/}
                </button>
                <button
                    onClick={() => handleSaveValue(0)}
                    style={{ width: '100%', height: 40 }}
                    className={`target-status-no-response-btn-box`}
                >
                    <img src={Images.remove} alt="mark" style={{ width: 20, height: 20 }} />

                    {/*{(!!loading && selectedValue === 0) ?*/}
                    {/*    <MinLoader margin={'0'} color={'#414651'} position={'relative'} />*/}
                    {/*    :*/}
                    {/*    <>*/}
                    {/*        <img src={Images.remove} alt="mark" style={{ width: 20, height: 20 }} />*/}
                    {/*        No*/}
                    {/*    </>*/}
                    {/*}*/}
                </button>
            </div>
        </div>
    );
};

export const GridWholeInterval = ({
                                      index,
                                      btnTitle,
                                      item,
                                      loader,
                                      disableActions,
                                      capMessage,
                                      saveTSummary,
                                      current,
                                      goal,
                                      column,
                                      setTimerInfo,
                                  }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openPopper = Boolean(anchorEl);
    const id = openPopper ? 'simple-popover' : undefined;
    const dispatch = useDispatch();
    const [timer, setTimer] = useState(null);
    const [play, setPlay] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [selectedValue, setSelectedValue] = useState(false);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    // Single-fire latch — see Latency for rationale; protects Yes vs No double-tap
    // and the auto-trigger modal racing a manual click after timer === maxTime.
    const submittingRef = useRef(false);
    // Guards against the hydrate effect re-starting the timer after a cancel.
    // Set true in handleCancel, cleared in WIND-DOWN (delete landed) or handlePlay.
    const cancellingRef = useRef(false);
    const intervalRef = useRef(null);
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);
    const sessionId = useSelector((state) => state.session.sessionById?.id);
    const loadDelete = FindLoad('DELETE_TRIAL_LOG');
    // Interval types (Whole/Partial/Momentary) use a SHARED trial across all
    // clinicians observing the same target — whoever starts the timer, anyone
    // can answer Yes/No. `findInProgressTrial` is called without a staffId so
    // it returns the room's single active trial, not just trials authored by
    // this user. Concurrent Yes/No clicks resolve via optimistic-concurrency
    // in `updateTrialAtomic` (first writer wins, second silently no-ops).
    const timerValue =
        goal?.dataType === goalDataTypeEnums.WHOLE_INTERVAL ||
        goal?.dataType === goalDataTypeEnums.PARTIAL_INTERVAL ||
        goal?.dataType === goalDataTypeEnums.MOMENTARY_TIME_SAMPLING
            ? goal?.options[0]
            : null;
    const maxTime = timerValue?.value;
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const text = renderIntervalText(goal?.dataType);
    const [showModal, setShowModal] = useState(false);
    const inProgress = findInProgressTrial(item);

    useEffect(() => {
        if (current?.id) {
            setTimer(null);
            setDisabled(true);
            setPlay(false);
            setSelectedValue(false);
            setActiveTrialId(null);
            startPromiseRef.current = null;
            submittingRef.current = false;
            // Cross-tab sync: if Tab A answered Yes/No, Tab B's auto-trigger
            // modal would otherwise stay open even though the trial is now
            // COMPLETED. Close it explicitly when current materialises.
            setShowModal(false);
        } else {
            setTimer(null);
            setPlay(false);
            setDisabled(false);
            setSelectedValue(false);
            setActiveTrialId(null);
            startPromiseRef.current = null;
            submittingRef.current = false;
            cancellingRef.current = false;
            setShowModal(false);
        }
    }, [current, index]);

    useEffect(() => {
        // Suppress modal pop while session is paused — clinician shouldn't be
        // forced to answer Yes/No mid-pause.
        if (!current && !disabled && timer === maxTime && !disableActions) {
            setShowModal(true);
            setDisabled(true);
            setPlay(false);
            setTimerInfo({});
        }
    }, [timer, current, maxTime, disableActions]);

    useEffect(() => {
        if (play && !current && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => {
                    if (prev + 1 >= maxTime) {
                        clearInterval(intervalRef.current);
                        setPlay(false);
                        return maxTime;
                    }
                    return prev + 1;
                });
            }, 1000);
        }

        return () => clearInterval(intervalRef.current);
    }, [play, current, maxTime, disableActions]);

    // Combined hydrate-or-sync — see WholeInterval for the three-branch rationale.
    useEffect(() => {
        if (!inProgress?.startTime) {
            if (activeTrialId || cancellingRef.current) {
                setActiveTrialId(null);
                setPlay(false);
                setTimer(null);
                setDisabled(false);
                setSelectedValue(false);
                setShowModal(false);
                startPromiseRef.current = null;
                submittingRef.current = false;
                cancellingRef.current = false;
                setTimerInfo({});
            }
            return;
        }
        const clamp = (v) => (maxTime ? Math.min(maxTime, Math.max(0, v)) : Math.max(0, v));
        const remoteIsPaused = !!inProgress.isPaused;

        // Skip re-hydration while a cancel is in-flight — the create response
        // can land before the delete, which would re-start the timer.
        if (cancellingRef.current) return;

        if (!activeTrialId || inProgress.id !== activeTrialId) {
            const checkpoint = Number(inProgress.value) || 0;
            const elapsedFromEvents = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
            const elapsed = remoteIsPaused ? clamp(checkpoint) : clamp(elapsedFromEvents);
            setActiveTrialId(inProgress.id);
            setStartEndTime({ startTime: inProgress.startTime, endTime: null });
            setTimer(elapsed);
            setShowModal(false);
            if (remoteIsPaused) {
                setPlay(false);
                setTimerInfo({});
            } else if (maxTime && elapsed >= maxTime) {
                setPlay(false);
            } else {
                setPlay(true);
                dispatch(httpRequestsOnLoadActions.appendLoading(item?.id, item));
                setTimerInfo({ ...item, active: true, startTime: inProgress.startTime });
            }
            return;
        }

        if (remoteIsPaused && play) {
            const checkpoint = Number(inProgress.value) || 0;
            setPlay(false);
            setTimer(clamp(checkpoint));
            setTimerInfo({});
        } else if (!remoteIsPaused && !play && timer !== null && (!maxTime || timer < maxTime)) {
            setPlay(true);
            setTimerInfo({ ...item, active: true, startTime: inProgress.startTime });
        }
    }, [item?.tSummary?.trials, maxTime, sessionEvents]);

    // Reuse an existing IN_PROGRESS trial — see WholeInterval.handlePlay for
    // the full rationale (local pause→resume, refresh, cross-tab).
    const handlePlay = () => {
        if (capMessage) return;
        if (disableActions) return;
        cancellingRef.current = false;
        const existing = findInProgressTrial(item);
        if (existing?.startTime) {
            const wasPaused = !!existing.isPaused;
            const checkpoint = wasPaused
                ? (existing.value ?? 0)
                : computeActiveElapsedSeconds(sessionEvents, existing.startTime, moment().format());
            const elapsed = maxTime ? Math.min(maxTime, Math.max(0, checkpoint)) : Math.max(0, checkpoint);
            const newStartTime = wasPaused ? moment().subtract(elapsed, 'seconds').format() : existing.startTime;
            setActiveTrialId(existing.id);
            setStartEndTime({ startTime: newStartTime, endTime: null });
            setTimer(elapsed);
            setPlay(true);
            setTimerInfo({ ...item, active: true, startTime: newStartTime });
            if (wasPaused) {
                saveTSummary({
                    trialId: existing.id,
                    value: elapsed,
                    startTime: newStartTime,
                    status: trialStatusEnums.IN_PROGRESS,
                    isPaused: false,
                });
            }
            return;
        }
        const startTime = moment().format();
        if (timer === null) setTimer(0);
        setPlay(true);
        setStartEndTime({ startTime, endTime: null });
        dispatch(httpRequestsOnLoadActions.appendLoading(item?.id, item));
        setTimerInfo({ ...item, active: true, startTime });
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    // Cancel — see WholeInterval.handleCancel.
    const handleCancel = async () => {
        cancellingRef.current = true;
        setPlay(false);
        setTimer(null);
        setDisabled(false);
        setSelectedValue(false);
        setStartEndTime({ startTime: null, endTime: null });
        setTimerInfo({});
        setShowModal(false);
        submittingRef.current = false;
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        setActiveTrialId(null);
        startPromiseRef.current = null;
        const deleteId = trialId || inProgress?.id;
        if (!deleteId || !item?.tSummary?.id) return;
        dispatch(sessionActions.deleteTrialLog(sessionId, item?.tSummary?.id, deleteId));
    };

    const handleSaveValue = async (value) => {
        if (submittingRef.current || disableActions) return;
        submittingRef.current = true;
        const endTime = moment().format();
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary(
            {
                trialId: trialId || undefined,
                value,
                startTime: startEndTime?.startTime,
                endTime,
                status: trialStatusEnums.COMPLETED,
            },
            handleClose,
        );
        setSelectedValue(value);
        setPlay(false);
        setTimer(null);
        setStartEndTime((prev) => ({ ...prev, endTime }));
        setActiveTrialId(null);
        startPromiseRef.current = null;
        setShowModal(false);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <div style={{ position: 'relative' }}>
                {/*<>*/}
                {/*{!timer && */}
                {/*<div className="task-card-complete-number" style={{ background: background }}>*/}
                {/*    <p style={{ color: color }}>{`${index}/${goal?.minTrials}`}</p>*/}
                {/*</div>*/}
                {/*}*/}
                {/*{timer &&*/}
                {/*    <div className="task-card-timer-box" style={{ marginTop: '-30px', width: 'auto', marginLeft: 20 }}>*/}
                {/*        {formatTime(timer)}*/}
                {/*    </div>*/}
                {/*}*/}
                {/*</>*/}

                <button aria-describedby={id} onClick={handleClick}>
                    {btnTitle && <p>{btnTitle}</p>}
                </button>

                <Popover
                    id={id}
                    open={openPopper}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            minWidth: 150,
                            borderRadius: '8px',
                        },
                    }}
                >
                    <div className="manage-actions-popover">
                        <div className="manage-actions-popover-body">
                            <div className="full-width">
                                <div className={column ? 'column' : 'flex-align-center'} style={{ gap: 16 }}>
                                    {!current && (
                                        <div>
                                            {
                                                <div className="flex-align-center" style={{ gap: 16 }}>
                                                    {!play ? (
                                                        <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                                                        <span style={{ display: 'inline-flex' }}>
                                                        <button
                                                            disabled={disableActions || loadDelete?.length || !!capMessage}
                                                            style={{ opacity: (disableActions || loadDelete?.length) || capMessage ? 0.5 : 1 }}
                                                            className="play-btn-box"
                                                            onClick={handlePlay}
                                                        >
                                                            <img src={Images.play} alt="play-icon" />
                                                        </button>
                                                        </span>
                                                        </SimpleTooltip>
                                                    ) : (
                                                        <button
                                                            disabled={!inProgress || loadDelete?.length}
                                                            style={{ opacity: !inProgress ? 0.5 : 1 }}
                                                            className="play-btn-box"
                                                            onClick={handleCancel}
                                                        >
                                                            <img
                                                                style={{
                                                                    filter: 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(7443%) hue-rotate(96deg) brightness(104%) contrast(101%)',
                                                                }}
                                                                src={Images.remove}
                                                                alt="cancel"
                                                            />
                                                        </button>
                                                    )}
                                                    <div className="timer-box">
                                                        {formatTime(timer)}
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    )}

                                    <div className="flex-align-center" style={{ gap: 16 }}>
                                        <button
                                            onClick={() => handleSaveValue(1)}
                                            style={{ opacity: !disabled || disableActions ? 0.5 : 1, width: 76 }}
                                            disabled={!disabled || disableActions}
                                            className={`target-status-blue-btn-box ${current?.value === 1 ? 'active' : ''}`}
                                        >
                                            {!!loading && selectedValue === 1 ? (
                                                <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                                            ) : (
                                                <>
                                                    <img src={Images.checkmarkWhite} alt="mark" />
                                                    Yes
                                                </>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleSaveValue(0)}
                                            style={{ opacity: !disabled || disableActions ? 0.5 : 1, width: 76 }}
                                            disabled={!disabled || disableActions}
                                            className={`target-status-no-response-btn-box ${current?.value === 0 ? 'active' : ''}`}
                                        >
                                            {!!loading && selectedValue === 0 ? (
                                                <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                                            ) : (
                                                <>
                                                    <img src={Images.remove} alt="mark"
                                                         style={{ width: 20, height: 20 }} />
                                                    No
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Popover>
            </div>

            <CustomModal
                handleOpenClose={() => setShowModal(false)}
                openDefault={showModal}
                content={
                    <div style={{ outline: 'none' }} className="modal-wrapper-style open-sans">
                        <div className="modal-body-style-small">
                            <IntervalEnded goal={goal} item={item} saveTSummary={handleSaveValue} />
                        </div>
                    </div>
                }
            />
        </>
    );
};

export const Rate = ({ item, loader, disableActions, capMessage, saveTSummary, index }) => {
    const [timer, setTimer] = useState(0);
    const [count, setCount] = useState(0);
    const [play, setPlay] = useState(false);
    const intervalRef = useRef(null);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const success = FindSuccessItem(item?.id)
    const dispatch = useDispatch();
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);
    // Rate uses a SHARED trial across all clinicians on the same target —
    // whoever starts, the team sees the same active trial / running count.
    // `findInProgressTrial` is called without a staffId so observers in
    // other tabs see Tab A's recording. Concurrent +/- click races resolve
    // last-writer-wins (debounced persistCount sends to the same trial).

    useEffect(() => {
        // if (loading?.targetId === item?.id) {
        if(success?.type === item?.id){
            dispatch(httpRequestsOnSuccessActions.removeSuccess(item?.id))
            setTimer(0);
            setCount(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
        }
    }, [success]);

    // useEffect(() => {
    //     if (loading?.targetId === item?.id) {
    //         setTimer(0);
    //         setCount(0);
    //     }
    // }, [index, item, loading]);

    // useEffect(() => {
    //     setCount(0);
    //     setTimer(0);
    // // }, []);
    // }, [item]);

    useEffect(() => {
        if (play && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [play, disableActions]);

    // Persist the running count back into the IN_PROGRESS trial's value. The
    // backend's updateTrial path leaves summary aggregates untouched while
    // status === IN_PROGRESS, so this is a safe partial save. Debounced 800ms
    // so a burst of +/- clicks coalesces into one network call. Pause guard:
    // backend will reject the save if the session has paused mid-debounce, so
    // we early-return here to avoid the toast error.
    const persistCount = useCallback(
        async (value) => {
            if (disableActions) return;
            let trialId = activeTrialId;
            if (!trialId && startPromiseRef.current) {
                trialId = await startPromiseRef.current;
            }
            if (!trialId || !startEndTime?.startTime) return;
            saveTSummary({
                trialId,
                value,
                startTime: startEndTime.startTime,
                status: trialStatusEnums.IN_PROGRESS,
            });
        },
        [activeTrialId, saveTSummary, startEndTime?.startTime, disableActions],
    );
    const [debouncedPersistCount, cancelPersistCount] = useDebouncedCallback(persistCount, 800);

    const updateCount = (next) => {
        const clamped = Math.max(0, Math.min(999, Number(next) || 0));
        setCount(clamped);
        debouncedPersistCount(clamped);
    };

    // Resume on mount — restores `count` from the persisted IN_PROGRESS value
    // and uses session events to subtract pause durations from the elapsed timer.
    // Picks up ANY IN_PROGRESS trial (no staff filter) so observers see the
    // active trial started by a colleague.
    useEffect(() => {
        if (play || activeTrialId) return;
        const inProgress = findInProgressTrial(item);
        if (!inProgress?.startTime) return;
        const elapsed = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
        setActiveTrialId(inProgress.id);
        setStartEndTime({ startTime: inProgress.startTime, endTime: null });
        setTimer(elapsed);
        setCount(Number(inProgress.value) || 0);
        setPlay(true);
    }, [item?.tSummary?.trials, sessionEvents]);

    // Live-sync from server when another device/tab edits the same trial. The
    // socket-driven session refetch updates `item.tSummary.trials`; this effect
    // mirrors any remote `value` change into local `count`. If the matching trial
    // is gone (someone else hit Stop, or it was deleted), we wind down locally
    // so a stale +/- click here can't demote the now-COMPLETED trial back to
    // IN_PROGRESS (the backend would happily do that — see updateTrial logic).
    const countRef = useRef(count);
    useEffect(() => { countRef.current = count; }, [count]);
    useEffect(() => {
        if (!play || !activeTrialId) return;
        const inProgress = findInProgressTrial(item);
        if (!inProgress || inProgress.id !== activeTrialId) {
            cancelPersistCount();
            setPlay(false);
            setTimer(0);
            setCount(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
            return;
        }
        const remoteValue = Number(inProgress.value) || 0;
        if (remoteValue !== countRef.current) {
            // Cancel any pending stale save first so the debounce doesn't fire
            // 800ms later with the pre-sync count and overwrite the remote bump.
            cancelPersistCount();
            setCount(remoteValue);
        }
    }, [item?.tSummary?.trials, play, activeTrialId, cancelPersistCount]);

    const handlePlay = () => {
        if (capMessage) return;
        const startTime = moment().format();
        setTimer(0);
        setCount(0);
        setPlay(true);
        setStartEndTime({ startTime, endTime: null });
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    const handleStop = async () => {
        // Cancel any pending count save so it can't race past the COMPLETED write
        // and demote a finalised trial back to IN_PROGRESS.
        cancelPersistCount();
        // Clock-skew guard: if startTime came from another machine's `now`
        // (resume-on-mount of a trial started in another tab/browser) and our
        // local clock trails it, `moment()` would produce an endTime that
        // predates the startTime — Rate's loggedTime aggregator (and the
        // history log) would then render a negative duration. Clamp endTime
        // to never go before startTime.
        const start = moment(startEndTime?.startTime);
        const now = moment();
        const endTime = start.isValid() && now.isBefore(start) ? start.format() : now.format();
        setPlay(false);
        setStartEndTime((prev) => ({ ...prev, endTime }));
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary({
            trialId: trialId || undefined,
            value: count,
            startTime: startEndTime?.startTime,
            endTime,
            status: trialStatusEnums.COMPLETED,
        });
        setActiveTrialId(null);
        startPromiseRef.current = null;
    };

    return (
        <div className="full-width flex-align-center" style={{ gap: 16 }}>
            <RateLoggedInfo item={item} />
            <div className={'space-between'} style={{ gap: 16 }}>
                <div className="flex-align-center" style={{ gap: 16 }}>
                    <div className="flex-align-center" style={{ gap: 16 }}>
                        {!play && !loading ? (
                            <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                            <span style={{ display: 'inline-flex' }}>
                            <button
                                className="play-btn-box"
                                disabled={disableActions || !!capMessage}
                                style={{ width: 40, opacity: disableActions || capMessage ? 0.5 : 1 }}
                                onClick={handlePlay}
                            >
                                <img src={Images.play} alt="play-icon" />
                            </button>
                            </span>
                            </SimpleTooltip>
                        ) : (
                            <button
                                disabled={loading}
                                style={{ height: 40, width: 40, opacity: loading ? 0.5 : 1 }}
                                className="session-gray-action-btn-box"
                                onClick={handleStop}
                            >
                                {loading ? (
                                    <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                                ) : (
                                    <img src={Images.pauseCircle} alt="pause" />
                                )}
                            </button>
                        )}
                        <div className="timer-box">{formatTime(timer)}</div>
                    </div>

                    <div className="flex-align-center">
                        <button
                            disabled={count === 0 || disableActions || !play}
                            style={{
                                height: 40,
                                width: 40,
                                minWidth: 40,
                                opacity: count === 0 || disableActions || !play ? 0.5 : 1,
                            }}
                            className="session-gray-action-btn-box"
                            onClick={() => updateCount(count - 1)}
                        >
                            <img src={Images.minusRemoveCircle} alt="resetTimmer" />
                        </button>
                        <input
                            style={{ width: 70, minWidth: 70 }}
                            disabled={disableActions || !play}
                            className="frequency-count-input-box"
                            type="number"
                            onChange={(e) => updateCount(e.target.value)}
                            value={count}
                        />
                        <button
                            disabled={disableActions || !play}
                            style={{ height: 40, width: 40, minWidth: 40, opacity: disableActions || !play ? 0.5 : 1 }}
                            className="session-gray-action-btn-box"
                            onClick={() => updateCount(count + 1)}
                        >
                            <img src={Images.addCircle} alt="resetTimmer" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const GridRate = ({ index, btnTitle, item, loader, disableActions, capMessage, saveTSummary, setTimerInfo }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [count, setCount] = useState(0);
    const openPopper = Boolean(anchorEl);
    const id = openPopper ? 'simple-popover' : undefined;
    const [timer, setTimer] = useState(0);
    const [play, setPlay] = useState(false);
    const intervalRef = useRef(null);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const success = FindSuccessItem(item?.id)
    const dispatch = useDispatch();
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);
    // Shared trial visibility — see Rate (list variant) for rationale.

    useEffect(() => {
            // if (loading?.targetId === item?.id) {
        if(success?.type === item?.id){
            dispatch(httpRequestsOnSuccessActions.removeSuccess(item?.id))
            setTimer(0);
            setCount(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
        }
    }, [success]);

    // useEffect(() => {
    //     // if(item){
    //     //     setCount(loggedCount || 0);
    //     // }else{
    //     //     setCount(0);
    //     // }
    //     setTimer(0);
    //     setCount(0);
    // }, [item]);

    useEffect(() => {
        if (play && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [play, disableActions]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Mirror Rate's debounced count persistence — see Rate (list variant) for
    // rationale + race notes. Pause guard prevents debounced save from racing
    // past a session pause and getting rejected.
    const persistCount = useCallback(
        async (value) => {
            if (disableActions) return;
            let trialId = activeTrialId;
            if (!trialId && startPromiseRef.current) {
                trialId = await startPromiseRef.current;
            }
            if (!trialId || !startEndTime?.startTime) return;
            saveTSummary({
                trialId,
                value,
                startTime: startEndTime.startTime,
                status: trialStatusEnums.IN_PROGRESS,
            });
        },
        [activeTrialId, saveTSummary, startEndTime?.startTime, disableActions],
    );
    const [debouncedPersistCount, cancelPersistCount] = useDebouncedCallback(persistCount, 800);

    const updateCount = (next) => {
        const clamped = Math.max(0, Math.min(999, Number(next) || 0));
        setCount(clamped);
        debouncedPersistCount(clamped);
    };

    // Resume on mount — restores `count` and uses session events for pause-aware
    // elapsed (see Rate list variant). Shared trial across clinicians.
    useEffect(() => {
        if (play || activeTrialId) return;
        const inProgress = findInProgressTrial(item);
        if (!inProgress?.startTime) return;
        const elapsed = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
        setActiveTrialId(inProgress.id);
        setStartEndTime({ startTime: inProgress.startTime, endTime: null });
        setTimer(elapsed);
        setCount(Number(inProgress.value) || 0);
        setPlay(true);
        setTimerInfo({ ...item, active: true, startTime: inProgress.startTime });
    }, [item?.tSummary?.trials, sessionEvents]);

    // Live-sync from server — see Rate (list variant) for full rationale.
    const countRef = useRef(count);
    useEffect(() => { countRef.current = count; }, [count]);
    useEffect(() => {
        if (!play || !activeTrialId) return;
        const inProgress = findInProgressTrial(item);
        if (!inProgress || inProgress.id !== activeTrialId) {
            cancelPersistCount();
            setPlay(false);
            setTimer(0);
            setCount(0);
            setActiveTrialId(null);
            startPromiseRef.current = null;
            return;
        }
        const remoteValue = Number(inProgress.value) || 0;
        if (remoteValue !== countRef.current) {
            cancelPersistCount();
            setCount(remoteValue);
        }
    }, [item?.tSummary?.trials, play, activeTrialId, cancelPersistCount]);

    const handlePlay = () => {
        if (capMessage) return;
        const startTime = moment().format();
        setTimer(0);
        setCount(0);
        setPlay(true);
        setStartEndTime({ startTime, endTime: null });
        setTimerInfo({ ...item, active: true, startTime });
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    const handleStop = async () => {
        cancelPersistCount();
        // Clock-skew guard — see Rate (list variant).
        const start = moment(startEndTime?.startTime);
        const now = moment();
        const endTime = start.isValid() && now.isBefore(start) ? start.format() : now.format();
        setPlay(false);
        setStartEndTime((prev) => ({ ...prev, endTime }));
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary(
            {
                trialId: trialId || undefined,
                value: count,
                startTime: startEndTime?.startTime,
                endTime,
                status: trialStatusEnums.COMPLETED,
            },
            handleClose,
        );
        setActiveTrialId(null);
        startPromiseRef.current = null;
    };

    return (
        <>
            <div style={{ position: 'relative' }}>
                <button aria-describedby={id} onClick={handleClick}>
                    {btnTitle && <p>{btnTitle}</p>}
                </button>
                <Popover
                    id={id}
                    open={openPopper}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            minWidth: 150,
                            borderRadius: '8px',
                        },
                    }}
                >
                    <div className="manage-actions-popover">
                        <div className="manage-actions-popover-body">
                            <div className="full-width">
                                <div className={'flex-align-center'} style={{ gap: 16 }}>
                                    <div className="flex-align-center" style={{ gap: 16 }}>
                                        <div className="flex-align-center" style={{ gap: 16 }}>
                                            {!play && !loading ? (
                                                <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                                                <span style={{ display: 'inline-flex' }}>
                                                <button
                                                    className="play-btn-box"
                                                    disabled={disableActions || !!capMessage}
                                                    style={{
                                                        width: 40,
                                                        opacity: disableActions || capMessage ? 0.5 : 1,
                                                    }}
                                                    onClick={handlePlay}
                                                >
                                                    <img src={Images.play} alt="play-icon" />
                                                </button>
                                                </span>
                                                </SimpleTooltip>
                                            ) : (
                                                <button
                                                    disabled={loading}
                                                    style={{ height: 40, width: 40, opacity: loading ? 0.5 : 1 }}
                                                    className="session-gray-action-btn-box"
                                                    onClick={handleStop}
                                                >
                                                    {loading ? (
                                                        <MinLoader margin={'0'} color={'#414651'}
                                                                   position={'relative'} />
                                                    ) : (
                                                        <img src={Images.pauseCircle} alt="pause" />
                                                    )}
                                                </button>
                                            )}
                                            {/*<div className="timer-box">{formatTime((loggedTime || 0) + timer)}</div>*/}
                                            <div className="timer-box">{formatTime(timer)}</div>
                                        </div>
                                        <div className="flex-align-center">
                                            <button
                                                disabled={count === 0 || disableActions || !play}
                                                style={{
                                                    height: 40,
                                                    width: 40,
                                                    minWidth: 40,
                                                    opacity: count === 0 || disableActions || !play ? 0.5 : 1,
                                                }}
                                                className="session-gray-action-btn-box"
                                                onClick={() => updateCount(count - 1)}
                                            >
                                                <img src={Images.minusRemoveCircle} alt="resetTimmer" />
                                            </button>
                                            <input
                                                disabled={disableActions || !play}
                                                className="frequency-count-input-box"
                                                type="number"
                                                onChange={(e) => updateCount(e.target.value)}
                                                value={count}
                                            />
                                            <button
                                                disabled={disableActions || !play}
                                                style={{
                                                    height: 40,
                                                    width: 40,
                                                    minWidth: 40,
                                                    opacity: disableActions || !play ? 0.5 : 1,
                                                }}
                                                className="session-gray-action-btn-box"
                                                onClick={() => updateCount(count + 1)}
                                            >
                                                <img src={Images.addCircle} alt="resetTimmer" />
                                            </button>
                                        </div>
                                    </div>

                                    {/*{!!item?.tSummary?.trials?.length && (*/}
                                    {/*    <button*/}
                                    {/*        style={{ height: 36, width: 36 }}*/}
                                    {/*        className="session-gray-action-btn-box"*/}
                                    {/*        onClick={() => handleDurationHistory(item)}*/}
                                    {/*    >*/}
                                    {/*        <img src={Images.historyTab} alt="resetTimmer" />*/}
                                    {/*    </button>*/}
                                    {/*)}*/}
                                </div>
                            </div>
                        </div>
                    </div>
                </Popover>
            </div>
        </>
    );
};

export const Irt = ({ item, loader, disableActions, capMessage, saveTSummary, current, index, setTimerInfo }) => {
    const [timer, setTimer] = useState(0);
    const [play, setPlay] = useState(false);
    const intervalRef = useRef(null);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const [startWorking, setStartWorking] = useState(!!item?.tSummary?.trials?.length);
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);
    const sessionId = useSelector((state) => state.session.sessionById?.id);
    const dispatch = useDispatch();

    // Local Pause/Resume in IRT toggles ticking only — but every Pause now
    // persists a value checkpoint to the IN_PROGRESS trial so refresh-after-
    // local-pause restores the correct elapsed (local pauses are NOT in the
    // session events, so the events-based math would over-count by the local
    // pause duration without this checkpoint).
    const startInProgress = (startTime) => {
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    // Send a value+isPaused snapshot. Pause click sets isPaused=true so refresh
    // restores the timer in frozen state; heartbeat / Resume sends isPaused=false
    // so a later refresh resumes ticking. Backend updateTrial is a no-op for
    // summary aggregates while status === IN_PROGRESS, so this is safe to call
    // repeatedly without inflating numTrials/value.
    const persistCheckpoint = (value, isPaused) => {
        if (disableActions || !activeTrialId || !startEndTime?.startTime) return;
        saveTSummary({
            trialId: activeTrialId,
            value,
            startTime: startEndTime.startTime,
            status: trialStatusEnums.IN_PROGRESS,
            isPaused,
        });
    };

    useEffect(() => {
        // Cross-tab guard: skip resetting timer/play/activeTrialId when an
        // active IN_PROGRESS trial is being mirrored (activeTrialId set). The
        // sync effect below owns that state in that case. Without this guard,
        // navigating to an empty slot would reset timer to 0 and Done would
        // save value=1, losing the seconds the remote trial had ticked.
        if (current) {
            if (activeTrialId) return;
            setTimer(current?.value);
            setPlay(false);
            setActiveTrialId(null);
            startPromiseRef.current = null;
        } else {
            if (activeTrialId) return;
            setTimer(0);
        }
    }, [current, index]);

    // Auto-restart for IRT now lives in the backend (tSummary.service.ts
    // updateTrial creates the next IN_PROGRESS in the same save when
    // goal.trialsPerSession isn't met). The new trial flows back via the
    // post-save tSummary and the socket fan-out, and this component's
    // sync effect picks it up. No frontend [success] auto-restart needed.

    useEffect(() => {
        if (play && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [play, disableActions]);

    // Resume on mount + live-sync: handles both the initial mount (no active
    // trial yet) AND ongoing updates when the IN_PROGRESS trial is mutated by
    // another tab or the user's own Pause click. Always mirrors server's
    // isPaused — without this, local play state could disagree with persisted
    // state (e.g. Pause-click race where local stays play=true even though save
    // succeeded with isPaused=true).
    useEffect(() => {
        const inProgress = findInProgressTrial(item);
        if (!inProgress?.startTime) {
            // No IN_PROGRESS exists (e.g. session reached trialsPerSession and
            // backend stopped auto-restarting). Wind down local state so the
            // timer doesn't keep ticking against a non-existent trial.
            if (activeTrialId) {
                setPlay(false);
                setTimer(0);
                setActiveTrialId(null);
                startPromiseRef.current = null;
                setTimerInfo?.({});
            }
            return;
        }
        const remoteIsPaused = !!inProgress.isPaused;

        // Hydrate when we have no active trial yet OR the active trial id has
        // changed — the latter happens after backend's IRT auto-restart, where
        // our previous IN_PROGRESS just became COMPLETED and a fresh one took
        // its place. Without this branch, Tab B would keep ticking against the
        // stale (now COMPLETED) trial id and show e.g. "5s" instead of resetting
        // to "0s" with the new trial.
        if (!activeTrialId || inProgress.id !== activeTrialId) {
            const elapsedFromEvents = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
            const checkpoint = Number(inProgress.value) || 0;
            const elapsed = checkpoint > 0 ? checkpoint : elapsedFromEvents;
            setActiveTrialId(inProgress.id);
            setStartEndTime({ startTime: inProgress.startTime, endTime: null });
            setTimer(elapsed);
            setPlay(!remoteIsPaused);
            setStartWorking(true);
            if (!remoteIsPaused) setTimerInfo?.({ ...item, active: true, startTime: inProgress.startTime });
            else setTimerInfo?.({});
            return;
        }

        // Ongoing sync: if server's isPaused disagrees with local play, force-sync.
        // This catches the case where Pause click set play=false but a later
        // re-render somehow flipped it back, OR another tab toggled the state.
        if (remoteIsPaused && play) {
            setPlay(false);
            setTimerInfo?.({});
            const checkpoint = Number(inProgress.value) || 0;
            if (checkpoint > 0) setTimer(checkpoint);
        } else if (!remoteIsPaused && !play && timer > 0) {
            // Server says running, local is paused — happens if another tab resumed.
            setPlay(true);
            setTimerInfo?.({ ...item, active: true, startTime: inProgress.startTime });
        }
    }, [item?.tSummary?.trials, sessionEvents]);

    const handleInitialPlay = () => {
        if (capMessage) return;
        if (disableActions) return;
        const startTime = moment().format();
        setTimer(0);
        setPlay(true);
        setStartWorking(true);
        setStartEndTime({ startTime, endTime: null });
        setTimerInfo?.({ ...item, active: true, startTime });
        startInProgress(startTime);
    };

    const handleDone = async () => {
        if (disableActions) return;
        const endTime = moment().format();
        setStartEndTime((prev) => ({ ...prev, endTime }));
        setPlay(false);
        setTimerInfo?.({});
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary({
            trialId: trialId || undefined,
            value: timer || 1,
            startTime: startEndTime?.startTime,
            endTime,
            status: trialStatusEnums.COMPLETED,
        });
        setActiveTrialId(null);
        startPromiseRef.current = null;
    };

    return (
        <div className="full-width">
            {!startWorking ? (
                    <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                    <span style={{ display: 'inline-flex' }}>
                    <button
                        className="play-btn-box"
                        disabled={disableActions || !!capMessage}
                        style={{ width: 40, opacity: disableActions || capMessage ? 0.5 : 1 }}
                        onClick={handleInitialPlay}
                    >
                        <img src={Images.play} alt="play-icon" />
                    </button>
                    </span>
                    </SimpleTooltip>
                ) : (
                    <div className="flex-align-center" style={{ gap: 16 }}>
                        {!current && (
                            play ? (
                                // Trial is running — Remove cancels and deletes it
                                <button
                                    disabled={loading || disableActions}
                                    style={{ opacity: (loading || disableActions) ? 0.5 : 1, width: 40, height: 40 }}
                                    className="session-gray-action-btn-box"
                                    onClick={() => {
                                        if (intervalRef.current) {
                                            clearInterval(intervalRef.current);
                                            intervalRef.current = null;
                                        }
                                        const trialId = activeTrialId;
                                        setPlay(false);
                                        setTimer(0);
                                        setActiveTrialId(null);
                                        startPromiseRef.current = null;
                                        setTimerInfo?.({});
                                        const hasCompletedTrials = item?.tSummary?.trials?.some(
                                            (t) => t?.status !== trialStatusEnums.IN_PROGRESS,
                                        );
                                        setStartWorking(!!hasCompletedTrials);
                                        if (trialId) {
                                            dispatch(sessionActions.deleteTrialLog(sessionId, item?.tSummary?.id, trialId));
                                        }
                                    }}
                                >
                                    <img src={Images.redTrash} alt="remove" style={{ width: 20, height: 20 }} />
                                </button>
                            ) : (
                                // No active trial — Start begins a new one
                                <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                                <span style={{ display: 'inline-flex' }}>
                                <button
                                    disabled={loading || disableActions || !!capMessage}
                                    style={{ width: 40, opacity: (loading || disableActions) || capMessage ? 0.5 : 1 }}
                                    className="play-btn-box"
                                    onClick={handleInitialPlay}
                                >
                                    <img src={Images.play} alt="start" />
                                </button>
                                </span>
                                </SimpleTooltip>
                            )
                        )}

                        <div className="timer-box">{formatTime(timer)}</div>

                        {!current && (
                            <button
                                disabled={loading || disableActions || timer === 0}
                                style={{
                                    background: 'rgba(230, 245, 238, 0.80)',
                                    height: 40,
                                    width: 40,
                                    opacity: loading || disableActions || timer === 0 ? 0.5 : 1,
                                }}
                                className="session-gray-action-btn-box"
                                onClick={handleDone}
                            >
                                {loading ? (
                                    <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                                ) : (
                                    <img
                                        src={Images.greenMark}
                                        alt="done"
                                        style={{
                                            filter: 'brightness(0) saturate(100%) invert(37%) sepia(45%) saturate(1177%) hue-rotate(109deg) brightness(97%) contrast(98%)',
                                            width: 20,
                                            height: 20,
                                        }}
                                    />
                                )}
                            </button>
                        )}
                    </div>
                )
            }
        </div>
    );
};

export const GridIrt = ({ index, btnTitle, item, loader, disableActions, capMessage, saveTSummary, current, setTimerInfo }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openPopper = Boolean(anchorEl);
    const id = openPopper ? 'simple-popover' : undefined;
    const [timer, setTimer] = useState(0);
    const [play, setPlay] = useState(false);
    const intervalRef = useRef(null);
    const [startEndTime, setStartEndTime] = useState({ startTime: null, endTime: null });
    const [activeTrialId, setActiveTrialId] = useState(null);
    const startPromiseRef = useRef(null);
    const loading = loader && loader?.find((i) => i?.params?.targetId === item?.id)?.params;
    const [startWorking, setStartWorking] = useState(!!item?.tSummary?.trials?.length);
    const sessionEvents = useSelector((state) => state.session.sessionById?.events);
    const sessionId = useSelector((state) => state.session.sessionById?.id);
    const dispatch = useDispatch();

    const startInProgress = (startTime) => {
        const promise = saveTSummary({
            value: 0,
            startTime,
            status: trialStatusEnums.IN_PROGRESS,
        })
            .then((tSummary) => {
                const id = findInProgressTrialId(tSummary);
                if (id) setActiveTrialId(id);
                return id;
            })
            .catch(() => null);
        startPromiseRef.current = promise;
    };

    // See Irt (list variant) for the full rationale on Pause checkpoint and the
    // isPaused flag.
    const persistCheckpoint = (value, isPaused) => {
        if (disableActions || !activeTrialId || !startEndTime?.startTime) return;
        saveTSummary({
            trialId: activeTrialId,
            value,
            startTime: startEndTime.startTime,
            status: trialStatusEnums.IN_PROGRESS,
            isPaused,
        });
    };

    useEffect(() => {
        // Cross-tab guard — see Irt (list variant): when activeTrialId is set
        // (synced from a remote IN_PROGRESS), skip resetting so the synced
        // timer/play state survives a navigation click.
        if (current) {
            if (activeTrialId) return;
            setTimer(current?.value);
            setPlay(false);
            setActiveTrialId(null);
            startPromiseRef.current = null;
        } else {
            if (activeTrialId) return;
            setTimer(0);
        }
    }, [current, index]);

    // Auto-restart for IRT lives in the backend now — see Irt (list variant).

    useEffect(() => {
        if (play && !disableActions) {
            intervalRef.current = setInterval(() => {
                setTimer((prev) => prev + 1);
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [play, disableActions]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Resume on mount + live-sync — see Irt (list variant) for full rationale.
    useEffect(() => {
        const inProgress = findInProgressTrial(item);
        if (!inProgress?.startTime) {
            if (activeTrialId) {
                setPlay(false);
                setTimer(0);
                setActiveTrialId(null);
                startPromiseRef.current = null;
                setTimerInfo?.({});
            }
            return;
        }
        const remoteIsPaused = !!inProgress.isPaused;

        // Hydrate when no active trial OR after backend's auto-restart switched
        // us to a fresh IN_PROGRESS — see Irt (list variant) for the trace.
        if (!activeTrialId || inProgress.id !== activeTrialId) {
            const elapsedFromEvents = computeActiveElapsedSeconds(sessionEvents, inProgress.startTime, moment().format());
            const checkpoint = Number(inProgress.value) || 0;
            const elapsed = checkpoint > 0 ? checkpoint : elapsedFromEvents;
            setActiveTrialId(inProgress.id);
            setStartEndTime({ startTime: inProgress.startTime, endTime: null });
            setTimer(elapsed);
            setPlay(!remoteIsPaused);
            setStartWorking(true);
            if (!remoteIsPaused) setTimerInfo?.({ ...item, active: true, startTime: inProgress.startTime });
            else setTimerInfo?.({});
            return;
        }

        if (remoteIsPaused && play) {
            setPlay(false);
            setTimerInfo?.({});
            const checkpoint = Number(inProgress.value) || 0;
            if (checkpoint > 0) setTimer(checkpoint);
        } else if (!remoteIsPaused && !play && timer > 0) {
            setPlay(true);
            setTimerInfo?.({ ...item, active: true, startTime: inProgress.startTime });
        }
    }, [item?.tSummary?.trials, sessionEvents]);

    const handleInitialPlay = () => {
        if (capMessage) return;
        if (disableActions) return;
        const startTime = moment().format();
        setTimer(0);
        setPlay(true);
        setStartWorking(true);
        setStartEndTime({ startTime, endTime: null });
        setTimerInfo?.({ ...item, active: true, startTime });
        startInProgress(startTime);
    };

    const handleDone = async () => {
        if (disableActions) return;
        const endTime = moment().format();
        setStartEndTime((prev) => ({ ...prev, endTime }));
        setPlay(false);
        setTimerInfo?.({});
        let trialId = activeTrialId;
        if (!trialId && startPromiseRef.current) {
            trialId = await startPromiseRef.current;
        }
        saveTSummary(
            {
                trialId: trialId || undefined,
                value: timer || 1,
                startTime: startEndTime?.startTime,
                endTime,
                status: trialStatusEnums.COMPLETED,
            },
            handleClose,
        );
        setActiveTrialId(null);
        startPromiseRef.current = null;
    };

    return (
        <>
            <div style={{ position: 'relative' }}>
                <button aria-describedby={id} onClick={handleClick}>
                    {btnTitle && <p>{btnTitle}</p>}
                </button>

                <Popover
                    id={id}
                    open={openPopper}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            minWidth: 150,
                            borderRadius: '8px',
                        },
                    }}
                >
                    <div className="manage-actions-popover">
                        <div className="manage-actions-popover-body">
                            <div className="full-width">
                                {!startWorking ? (
                                    <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                                    <span style={{ display: 'inline-flex' }}>
                                    <button
                                        className="play-btn-box"
                                        disabled={disableActions || !!capMessage}
                                        style={{ width: 40, opacity: disableActions || capMessage ? 0.5 : 1 }}
                                        onClick={handleInitialPlay}
                                    >
                                        <img src={Images.play} alt="play-icon" />
                                    </button>
                                    </span>
                                    </SimpleTooltip>
                                ) : (
                                    <div className="flex-align-center" style={{ gap: 16 }}>
                                        {!current && (
                                            play ? (
                                                // Trial is running — Remove cancels and deletes it
                                                <button
                                                    disabled={loading}
                                                    style={{ opacity: loading ? 0.5 : 1, width: 40, height: 40 }}
                                                    className="session-gray-action-btn-box"
                                                    onClick={() => {
                                                        if (intervalRef.current) {
                                                            clearInterval(intervalRef.current);
                                                            intervalRef.current = null;
                                                        }
                                                        const trialId = activeTrialId;
                                                        setPlay(false);
                                                        setTimer(0);
                                                        setActiveTrialId(null);
                                                        startPromiseRef.current = null;
                                                        setTimerInfo?.({});
                                                        const hasCompletedTrials = item?.tSummary?.trials?.some(
                                                            (t) => t?.status !== trialStatusEnums.IN_PROGRESS,
                                                        );
                                                        setStartWorking(!!hasCompletedTrials);
                                                        if (trialId) {
                                                            dispatch(
                                                                sessionActions.deleteTrialLog(
                                                                    sessionId,
                                                                    item?.tSummary?.id,
                                                                    trialId,
                                                                ),
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <img src={Images.redTrash} alt="remove" style={{ width: 20, height: 20 }} />
                                                </button>
                                            ) : (
                                                // No active trial — Start begins a new one
                                                <SimpleTooltip title={capMessage || ''} placement="top" arrow>
                                                <span style={{ display: 'inline-flex' }}>
                                                <button
                                                    disabled={loading || disableActions || !!capMessage}
                                                    style={{ width: 40, opacity: (loading || disableActions) || capMessage ? 0.5 : 1 }}
                                                    className="play-btn-box"
                                                    onClick={handleInitialPlay}
                                                >
                                                    <img src={Images.play} alt="start" />
                                                </button>
                                                </span>
                                                </SimpleTooltip>
                                            )
                                        )}

                                        <div className="timer-box">{formatTime(timer)}</div>

                                        {!current && (
                                            <button
                                                disabled={loading || disableActions || timer === 0}
                                                style={{
                                                    background: 'rgba(230, 245, 238, 0.80)',
                                                    height: 40,
                                                    width: 40,
                                                    opacity: loading || disableActions || timer === 0 ? 0.5 : 1,
                                                }}
                                                className="session-gray-action-btn-box"
                                                onClick={handleDone}
                                            >
                                                {loading ? (
                                                    <MinLoader margin={'0'} color={'#414651'} position={'relative'} />
                                                ) : (
                                                    <img
                                                        src={Images.greenMark}
                                                        alt="done"
                                                        style={{
                                                            filter: 'brightness(0) saturate(100%) invert(37%) sepia(45%) saturate(1177%) hue-rotate(109deg) brightness(97%) contrast(98%)',
                                                            width: 20,
                                                            height: 20,
                                                        }}
                                                    />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Popover>
            </div>
        </>
    );
};

export const RateLoggedInfo = ({ item }) => {
    // Clamp per-trial diff to >= 0. Cross-tab clock skew (Tab B's handleStop
    // uses Tab B's `now` as endTime against Tab A's startTime) can produce
    // negative durations on save — render those as 0 so the aggregate doesn't
    // go negative (which would render as "-1:-1:-20" via padStart on a small
    // negative total).
    const loggedTime = item?.tSummary?.trials?.reduce((acc, trial) => {
        if (!trial?.startTime || !trial?.endTime) return acc;
        const startMoment = moment(trial.startTime);
        const endMoment = moment(trial.endTime);
        const differenceInSeconds = endMoment.diff(startMoment, 'seconds');
        return acc + Math.max(0, differenceInSeconds);
    }, 0);
    const loggedCount = item?.tSummary?.trials?.reduce((acc, trial) => {
        if (!trial?.value) return acc;
        return acc + trial?.value;
    }, 0);

    function formatSecond(seconds) {
        const totalSeconds = Math.floor(Number(seconds) || 0);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;

        return [h.toString().padStart(2, '0'), m.toString().padStart(2, '0'), s.toString().padStart(2, '0')].join(':');
    }

    return (
        <div className="flex-align-center" style={{ gap: 16 }}>
            <div className="rate-logged-time-box">{formatSecond(loggedTime || 0)}</div>
            <div className="logged-count-box">{loggedCount || 0}</div>
        </div>
    );
};

export const checkPrevNext = (goal) => {
    return (
        goal?.dataType !== goalDataTypeEnums.DURATION &&
        goal?.dataType !== goalDataTypeEnums.RATE &&
        goal?.dataType !== goalDataTypeEnums.FREQUENCY
    );
};

export const intervalActions = {
    DISABLE_PREV_NEXT: 'DISABLE_PREV_NEXT',
    TIMER_IS_STARTED: 'TIMER_IS_STARTED',
    INTERVAL_FINISHED: 'INTERVAL_FINISHED',
};
